import { useState, useRef, useEffect, MutableRefObject } from "react";
import { useHover } from "usehooks-ts";
import { Project } from "shared/datamodel/schemas/project";
import { addToLocalStorageArray, getLocalStorageItem, setLocalStorageItem } from "frontend/utils/storage-utils";
import { TeamWithProjects } from "shared/datamodel/schemas/team";
import { useTeamsWithProjects } from "frontend/hooks/caching/use-teams-with-projects";
import consts, { SelectedTeamsAndProjects, TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY } from "shared/consts";
import ProjectList from "./projects-list";
import style from "./team-project-hierarchy.module.css";
import { WorkspaceCellNew } from "./team-cell-new";
import useAbility from "frontend/hooks/use-ability";
import useStateValue from "frontend/state/value";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { CreateProject } from "./create-project";
import { CanvasTemplate, TipName } from "shared/datamodel/schemas";
import { useTeamsCache } from "frontend/hooks/caching/use-teams-cache";
import { OpenTeamIcon } from "frontend/icons/open-team";
import { CreateWorkspace } from "./create-team";
import classNames from "classnames";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import tracking from "frontend/tracking";
import { CreateWorkspaceIcon } from "frontend/icons/create-workspace";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { WalkthroughTipType } from "frontend/walkthrough-tips/walkthrough-tip-model";
import { NoWorkspacesIcon } from "frontend/icons/no-workspaces";
import { AddProjectIcon } from "frontend/icons/add-project";
import Skeleton from "react-loading-skeleton";

export function WorkspaceProjectHierarchy({
  selectedTeamId,
  setSelectedTeam,
  selectedProjectId,
  setSelectedProject,
  resetSelectedInstance,
  onEditSeats,
  setShowRestrictedModal,
  handleNoPermissionToCreate,
  createNewBoard,
  simpleBarRef,
  setIsMovingBoards,
}: {
  selectedTeamId: string | null;
  setSelectedTeam: (team: TeamWithProjects) => void;
  selectedProjectId: string | null;
  setSelectedProject: (projectId: string, urlId?: string) => void;
  resetSelectedInstance: () => void;
  onEditSeats: () => void;
  setShowRestrictedModal: (show: boolean) => void;
  handleNoPermissionToCreate: () => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  simpleBarRef: MutableRefObject<HTMLDivElement | null>;
  setIsMovingBoards: (value: boolean) => void;
}) {
  const [{ user }] = useStateValue();
  const [expandedTeams, setExpandedTeams] = useState<string[]>([]);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [projectParentTeamId, setProjectParentTeamId] = useState<string | null>(null);
  const { canPerformAnyAction, canCreateInstance } = useAbility();
  const { getTeams } = useTeamsCache();
  const newTeamRef = useRef<HTMLDivElement | null>(null);
  const hover = useHover(newTeamRef);
  const { teamsWithProjects } = useTeamsWithProjects();
  const teamsWithProjectsArray = teamsWithProjects ?? [];

  const isTeamExpanded = (teamId: string) => {
    return expandedTeams.includes(teamId);
  };

  const expandTeam = (teamId: string) => {
    if (!isTeamExpanded(teamId)) {
      setExpandedTeams((previousTeams) => [...previousTeams, teamId]);
    }
  };

  const collapseTeam = (teamId: string) => {
    if (isTeamExpanded(teamId)) {
      setExpandedTeams((previousTeams) => previousTeams.filter((id) => id !== teamId));
    }
  };

  const toggleTeam = (teamId: string) => {
    if (isTeamExpanded(teamId)) {
      collapseTeam(teamId);
    } else {
      expandTeam(teamId);
    }
  };

  useEffect(() => {
    if (selectedProjectId && expandedTeams.length === 0) {
      const team = teamsWithProjectsArray.find((team) =>
        team.projects?.find((project) => project.documentId === selectedProjectId)
      );
      if (team) {
        setExpandedTeams([team.documentId]);
      }
    }
  }, [selectedProjectId, teamsWithProjects]);

  const handleProjectSelection = (project: Project) => {
    setSelectedProject(project.documentId, project.urlId);
    const selectedTeamsAndProjects = getLocalStorageItem<SelectedTeamsAndProjects>(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY
    );
    setLocalStorageItem(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY,
      selectedTeamsAndProjects?.filter((item) => item.selectedTeam !== selectedTeamId)
    );
    addToLocalStorageArray(TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY, {
      selectedTeam: selectedTeamId,
      selectedProject: project.documentId,
    });
  };

  function renderProjects(team: TeamWithProjects) {
    return (
      <div
        className={classNames(style.projectList, {
          [style.expanded]: isTeamExpanded(team.documentId),
        })}
      >
        <ProjectList
          key={team.documentId}
          team={team}
          selectedProjectId={selectedProjectId}
          handleProjectSelection={handleProjectSelection}
          onEditSeats={onEditSeats}
          createNewBoard={createNewBoard}
          onDeleteSelectedProject={resetSelectedInstance}
          handleNewProjectClick={handleNewProjectClick}
        />
      </div>
    );
  }

  const [isLoadingTeam, setIsLoadingTeam] = useState(false);

  async function handleNewProjectClick(parentTeamId: string) {
    setIsLoadingTeam(true);
    const teams = await getTeams([parentTeamId]);
    setIsLoadingTeam(false);

    if (!teams || teams.length === 0) return;
    const fullTeam = teams[0];

    const shouldShoWRestrictedModal = user && !canPerformAnyAction(user, fullTeam, InstanceType.Workspace);
    if (shouldShoWRestrictedModal) {
      setShowRestrictedModal(true);
    } else if (user && !canCreateInstance(user, fullTeam, InstanceType.Workspace)) {
      handleNoPermissionToCreate();
    } else {
      setProjectParentTeamId(parentTeamId);
      setShowCreateProjectModal(true);
    }
  }

  const handleTeamSelection = (team: TeamWithProjects) => {
    setSelectedTeam(team);
    const selectedTeamsAndProjects = getLocalStorageItem<SelectedTeamsAndProjects>(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY
    );
    const selectedProjectId = selectedTeamsAndProjects?.find(
      (item) => item.selectedTeam === team.documentId
    )?.selectedProject;
    setLocalStorageItem(
      TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY,
      selectedTeamsAndProjects?.filter((item) => item.selectedTeam !== team.documentId)
    );
    addToLocalStorageArray(TEAMS_AND_PROJECTS_LOCAL_STORAGE_KEY, {
      selectedTeam: team.documentId,
      selectedProject: selectedProjectId,
    });
  };

  const handleWorkspaceCreated = () => {
    if (simpleBarRef.current) {
      setTimeout(() => {
        simpleBarRef.current?.scrollTo({
          top: simpleBarRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
    }
  };

  const renderWorkspaces = () => {
    if (teamsWithProjects === null) {
      return (
        <>
          <Skeleton
            containerClassName={style.teamsContainer}
            className={style.teamHeader}
            count={4}
            style={{ marginBottom: "-16px" }}
          />
        </>
      );
    }
    if (teamsWithProjects.length === 0) {
      return (
        <div className={style.noWorkspaces} data-testid="no-workspaces">
          <NoWorkspacesIcon />
          <p>You don’t have a workspace yet</p>
          <div
            className={style.addWorkspaceButton}
            onClick={() => {
              tracking.trackAnalyticsEvent("create-workspace-clicked", {
                category: consts.TRACKING_CATEGORY.TEAM_ACTION,
                isNewHomePage: true,
              });
              const shouldShowRestrictedModal = !user?.isSubscribedToCanvas;
              if (shouldShowRestrictedModal) {
                setShowRestrictedModal(true);
              } else {
                setShowCreateTeamModal(true);
              }
            }}
            data-testid="empty-state-add-workspace-button"
          >
            <AddProjectIcon />
            <span>Add workspace</span>
          </div>
        </div>
      );
    }

    return (
      <div data-testid="workspaces-list">
        {teamsWithProjectsArray.map((team) => (
          <div key={team.documentId} className={style.teamContainer}>
            <div
              className={classNames(style.teamHeader, { [style.selectedTeam]: selectedTeamId === team.documentId })}
              onClick={() => {
                handleTeamSelection(team);
                expandTeam(team.documentId);
              }}
              data-testid={team.name}
            >
              <WorkspaceCellNew key={team.documentId} team={team} />
              <div
                className={style.teamToggle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTeam(team.documentId);
                }}
                data-testid={`workspace-toggle-${team.name}`}
              >
                <div
                  className={classNames(style.teamExpandButton, { [style.expanded]: isTeamExpanded(team.documentId) })}
                >
                  <OpenTeamIcon />
                </div>
              </div>
            </div>
            {isTeamExpanded(team.documentId) && renderProjects(team)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className={style.container}>
        <div className={style.title}>
          <div className={style.workspacesTitle} data-testid="workspaces-title">
            <span>WORKSPACES</span>
          </div>
          <div
            className={style.newTeam}
            onClick={() => {
              tracking.trackAnalyticsEvent("create-workspace-clicked", {
                category: consts.TRACKING_CATEGORY.TEAM_ACTION,
                isNewHomePage: true,
              });
              const shouldShoWRestrictedModal = !user?.isSubscribedToCanvas;
              if (shouldShoWRestrictedModal) {
                setShowRestrictedModal(true);
              } else {
                setShowCreateTeamModal(true);
              }
            }}
            ref={newTeamRef}
            data-testid="add-workspace-button"
          >
            {hover && (
              <Tooltip
                side="top"
                label={"Create new workspace"}
                relativeTo={newTeamRef}
                customStyle={{
                  backgroundColor: "#113357",
                  borderRadius: 4,
                  color: " #FFF",
                  fontSize: 12,
                  fontWeight: 300,
                }}
              />
            )}
            <CreateWorkspaceIcon />
          </div>
        </div>
        <div className={style.teamsContainer}>{renderWorkspaces()}</div>
      </div>
      {showCreateProjectModal && projectParentTeamId && !isLoadingTeam && (
        <CreateProject
          parentTeamId={projectParentTeamId}
          createNewBoard={createNewBoard}
          onEditSeats={onEditSeats}
          setSelectedProject={setSelectedProject}
          setIsMovingBoards={setIsMovingBoards}
        />
      )}
      {showCreateTeamModal && (
        <CreateWorkspace
          onEditSeats={onEditSeats}
          onCreate={handleWorkspaceCreated}
          onDismiss={() => setShowCreateTeamModal(false)}
          setSelectedTeam={setSelectedTeam}
        />
      )}
      <WalkthroughTipPresenter
        tipName={TipName.walkthroughTeamsAndProjects}
        attachToRef={newTeamRef}
        side="right"
        type={WalkthroughTipType.HomePage}
      />
    </>
  );
}
